/**
 * useBehaviorAnalysis Hook
 *
 * Analyzes trading behavior patterns - size impact, duration, momentum, etc.
 */

import { useMemo } from "react";
import type {
  CompletedTrade,
  BehaviorAnalysis,
  SizeBucket,
  DurationBucket,
} from "@nordex/core";

/**
 * Size bucket definitions (in USD volume)
 */
const SIZE_BUCKETS: Array<{
  label: string;
  minVolume: number;
  maxVolume: number;
}> = [
  { label: "Small", minVolume: 0, maxVolume: 1000 },
  { label: "Medium", minVolume: 1000, maxVolume: 10000 },
  { label: "Large", minVolume: 10000, maxVolume: 50000 },
  { label: "XL", minVolume: 50000, maxVolume: Infinity },
];

/**
 * Duration bucket definitions (in milliseconds)
 */
const DURATION_BUCKETS: Array<{
  label: string;
  minMs: number;
  maxMs: number;
}> = [
  { label: "< 1 min", minMs: 0, maxMs: 60 * 1000 },
  { label: "1-5 min", minMs: 60 * 1000, maxMs: 5 * 60 * 1000 },
  { label: "5-30 min", minMs: 5 * 60 * 1000, maxMs: 30 * 60 * 1000 },
  { label: "30m-1h", minMs: 30 * 60 * 1000, maxMs: 60 * 60 * 1000 },
  { label: "1-4h", minMs: 60 * 60 * 1000, maxMs: 4 * 60 * 60 * 1000 },
  { label: "4h+", minMs: 4 * 60 * 60 * 1000, maxMs: Infinity },
];

/**
 * Calculate coefficient of variation for position sizing consistency
 * Lower = more consistent
 */
function calculateSizeConsistency(volumes: number[]): number {
  if (volumes.length < 2) return 0;

  const mean = volumes.reduce((a, b) => a + b, 0) / volumes.length;
  if (mean === 0) return 0;

  const variance =
    volumes.reduce((sum, v) => sum + Math.pow(v - mean, 2), 0) / volumes.length;
  const stdDev = Math.sqrt(variance);

  // Coefficient of variation (0-100 scale, capped)
  const cv = (stdDev / mean) * 100;
  return Math.min(cv, 100);
}

/**
 * Calculate overtrading score
 * Based on correlation between trade frequency and negative P&L
 */
function calculateOvertradingScore(trades: CompletedTrade[]): number {
  if (trades.length < 10) return 0;

  // Group trades by day
  const dailyTrades = new Map<string, CompletedTrade[]>();
  for (const trade of trades) {
    const day = trade.exitTime.toISOString().split("T")[0];
    const existing = dailyTrades.get(day) || [];
    existing.push(trade);
    dailyTrades.set(day, existing);
  }

  // Calculate average trades per day
  const tradeCounts = Array.from(dailyTrades.values()).map((t) => t.length);
  const avgTrades =
    tradeCounts.reduce((a, b) => a + b, 0) / tradeCounts.length;

  // Compare P&L on high-trade days vs low-trade days
  let highTradeDayPnl = 0;
  let highTradeDays = 0;
  let lowTradeDayPnl = 0;
  let lowTradeDays = 0;

  for (const [_, dayTrades] of dailyTrades) {
    const dayPnl = dayTrades.reduce((sum, t) => sum + t.netPnl, 0);
    if (dayTrades.length >= avgTrades * 1.5) {
      highTradeDayPnl += dayPnl;
      highTradeDays++;
    } else {
      lowTradeDayPnl += dayPnl;
      lowTradeDays++;
    }
  }

  const avgHighDayPnl = highTradeDays > 0 ? highTradeDayPnl / highTradeDays : 0;
  const avgLowDayPnl = lowTradeDays > 0 ? lowTradeDayPnl / lowTradeDays : 0;

  // Score: 0 = no overtrading, 100 = severe overtrading
  // If high-trade days perform worse, that's overtrading
  if (avgHighDayPnl >= avgLowDayPnl) return 0;

  const diff = avgLowDayPnl - avgHighDayPnl;
  const maxDiff = Math.abs(avgLowDayPnl) + Math.abs(avgHighDayPnl);
  if (maxDiff === 0) return 0;

  return Math.min((diff / maxDiff) * 100, 100);
}

/**
 * Calculate behavior analysis
 */
function calculateBehaviorAnalysis(trades: CompletedTrade[]): BehaviorAnalysis {
  // Size impact
  const sizeImpact: SizeBucket[] = SIZE_BUCKETS.map((bucket) => {
    const bucketTrades = trades.filter(
      (t) => t.volume >= bucket.minVolume && t.volume < bucket.maxVolume
    );
    const wins = bucketTrades.filter((t) => t.netPnl > 0).length;
    const pnl = bucketTrades.reduce((sum, t) => sum + t.netPnl, 0);

    return {
      label: bucket.label,
      minVolume: bucket.minVolume,
      maxVolume: bucket.maxVolume,
      trades: bucketTrades.length,
      wins,
      winRate:
        bucketTrades.length > 0 ? (wins / bucketTrades.length) * 100 : 0,
      pnl,
      avgPnl: bucketTrades.length > 0 ? pnl / bucketTrades.length : 0,
    };
  });

  // Duration impact
  const durationImpact: DurationBucket[] = DURATION_BUCKETS.map((bucket) => {
    const bucketTrades = trades.filter(
      (t) => t.durationMs >= bucket.minMs && t.durationMs < bucket.maxMs
    );
    const wins = bucketTrades.filter((t) => t.netPnl > 0).length;
    const pnl = bucketTrades.reduce((sum, t) => sum + t.netPnl, 0);

    return {
      label: bucket.label,
      minMs: bucket.minMs,
      maxMs: bucket.maxMs,
      trades: bucketTrades.length,
      winRate:
        bucketTrades.length > 0 ? (wins / bucketTrades.length) * 100 : 0,
      pnl,
      avgPnl: bucketTrades.length > 0 ? pnl / bucketTrades.length : 0,
    };
  });

  // Win rate after wins/losses
  const sortedTrades = [...trades].sort(
    (a, b) => a.exitTime.getTime() - b.exitTime.getTime()
  );

  let tradesAfterWin = 0;
  let winsAfterWin = 0;
  let tradesAfterLoss = 0;
  let winsAfterLoss = 0;

  for (let i = 1; i < sortedTrades.length; i++) {
    const prevTrade = sortedTrades[i - 1];
    const currTrade = sortedTrades[i];
    const currIsWin = currTrade.netPnl > 0;

    if (prevTrade.netPnl > 0) {
      tradesAfterWin++;
      if (currIsWin) winsAfterWin++;
    } else if (prevTrade.netPnl < 0) {
      tradesAfterLoss++;
      if (currIsWin) winsAfterLoss++;
    }
  }

  const winRateAfterWin =
    tradesAfterWin > 0 ? (winsAfterWin / tradesAfterWin) * 100 : 0;
  const winRateAfterLoss =
    tradesAfterLoss > 0 ? (winsAfterLoss / tradesAfterLoss) * 100 : 0;

  // Long vs Short
  const longTrades = trades.filter((t) => t.side === "long");
  const shortTrades = trades.filter((t) => t.side === "short");

  const calcSideStats = (sideTrades: CompletedTrade[]) => {
    const wins = sideTrades.filter((t) => t.netPnl > 0).length;
    const pnl = sideTrades.reduce((sum, t) => sum + t.netPnl, 0);
    const profit = sideTrades
      .filter((t) => t.netPnl > 0)
      .reduce((sum, t) => sum + t.netPnl, 0);
    const loss = Math.abs(
      sideTrades
        .filter((t) => t.netPnl < 0)
        .reduce((sum, t) => sum + t.netPnl, 0)
    );

    return {
      trades: sideTrades.length,
      wins,
      winRate: sideTrades.length > 0 ? (wins / sideTrades.length) * 100 : 0,
      pnl,
      profitFactor: loss > 0 ? profit / loss : profit > 0 ? Infinity : 0,
    };
  };

  const longStats = calcSideStats(longTrades);
  const shortStats = calcSideStats(shortTrades);

  // Average duration
  const totalDuration = trades.reduce((sum, t) => sum + t.durationMs, 0);
  const avgDurationMs = trades.length > 0 ? totalDuration / trades.length : 0;

  // Position sizing consistency
  const volumes = trades.map((t) => t.volume);
  const sizeConsistencyScore = calculateSizeConsistency(volumes);

  // Overtrading score
  const overtradingScore = calculateOvertradingScore(trades);

  return {
    sizeImpact,
    durationImpact,
    winRateAfterWin,
    winRateAfterLoss,
    longStats,
    shortStats,
    avgDurationMs,
    sizeConsistencyScore,
    overtradingScore,
  };
}

/**
 * Hook for behavior analysis
 */
export function useBehaviorAnalysis(
  trades: CompletedTrade[]
): BehaviorAnalysis {
  return useMemo(() => calculateBehaviorAnalysis(trades), [trades]);
}

/**
 * Format duration for display
 */
export function formatDuration(ms: number): string {
  if (ms < 60 * 1000) {
    return `${Math.round(ms / 1000)}s`;
  }
  if (ms < 60 * 60 * 1000) {
    return `${Math.round(ms / (60 * 1000))}m`;
  }
  if (ms < 24 * 60 * 60 * 1000) {
    const hours = Math.floor(ms / (60 * 60 * 1000));
    const mins = Math.round((ms % (60 * 60 * 1000)) / (60 * 1000));
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  }
  const days = Math.round(ms / (24 * 60 * 60 * 1000));
  return `${days}d`;
}

